import bannedKeywordsData from '../assets/data/banned_keywords.json';

const BANNED_KEYWORDS = bannedKeywordsData.keywords.sort((a, b) => b.length - a.length);
const HIGHLIGHT_STYLE = 'background-color: #ffebee; color: #c62828; padding: 2px 4px; border-radius: 3px; font-weight: bold;';

const escapeRegex = (str) => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const createWordRegex = (keyword) => new RegExp(`\\b${escapeRegex(keyword)}\\b`, 'gi');
const createTestRegex = (keyword) => new RegExp(`\\b${escapeRegex(keyword)}\\b`, 'i');

// Escape HTML entities so untrusted text can't inject markup when
// the result is used with dangerouslySetInnerHTML.
const escapeHtml = (str) =>
  str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;');

export const highlightBannedWords = (text) => {
  if (!text) return text;
  // Escape first so any HTML in the title becomes inert, then wrap
  // matched banned words in hardcoded <span> tags (safe, not from user input).
  const escaped = escapeHtml(text);
  return BANNED_KEYWORDS.reduce((result, keyword) => {
    return result.replace(createWordRegex(keyword), `<span style="${HIGHLIGHT_STYLE}">$&</span>`);
  }, escaped);
};

export const containsBannedWords = (text) => {
  if (!text) return false;
  return BANNED_KEYWORDS.some(keyword => createTestRegex(keyword).test(text));
};

// Amazon 27-Jul-2026 content model: non-media titles are hard-capped at 75
// characters and a new searchable Item Highlights field allows up to 125.
export const TITLE_MAX_LENGTH = 75;
export const ITEM_HIGHLIGHTS_MAX_LENGTH = 125;

// A listing predates Amazon's 31-Jul-2026 modular title format when it carries no
// Item Highlights at all. This is read from the content rather than a timestamp:
// it needs no created_at plumbing, and it can never hide highlights that do exist.
export const usesLegacyTitleFormat = (itemHighlights) => !String(itemHighlights || '').trim();

// Single source for the wording, so the inline notice and the pre-push popups on both
// the single and bulk results cards always say the same thing.
export const LEGACY_FORMAT_MESSAGE = `Amazon has changed the title format — titles are now capped at ${TITLE_MAX_LENGTH} characters with a separate Item Highlights field of up to ${ITEM_HIGHLIGHTS_MAX_LENGTH}. This listing still uses the old format.`;

// Title validation functions
export const getTitleLength = (title) => title?.length || 0;

export const isTitleTooLong = (title, maxLength = TITLE_MAX_LENGTH) => getTitleLength(title) > maxLength;

export const validateTitleForAmazon = (title, maxLength = TITLE_MAX_LENGTH) => {
  const length = getTitleLength(title);
  return { length, isValid: length <= maxLength, isTooLong: length > maxLength, maxLength };
};

// Item Highlights validation
export const getItemHighlightsLength = (text) => text?.length || 0;

export const isItemHighlightsTooLong = (text, maxLength = ITEM_HIGHLIGHTS_MAX_LENGTH) =>
  getItemHighlightsLength(text) > maxLength;

// Detect keyword overlap between the title and item highlights. Both fields
// share the searchable space, so repeating a term wastes characters. Returns
// the list of significant words (>3 chars, minus common stop words) that
// appear in both, normalised for case and simple plural/stem form.
const _OVERLAP_STOP_WORDS = new Set([
  'the', 'and', 'for', 'with', 'from', 'this', 'that', 'your', 'our',
  'pack', 'set', 'new', 'pcs', 'pieces', 'piece',
]);

const _normalizeWord = (w) => w.toLowerCase().replace(/[^a-z0-9]/g, '').replace(/(ies|es|s)$/,'');

export const getTitleHighlightsOverlap = (title, highlights) => {
  if (!title || !highlights) return [];
  const toWords = (text) =>
    new Set(
      text
        .split(/\s+/)
        .map(_normalizeWord)
        .filter((w) => w.length > 3 && !_OVERLAP_STOP_WORDS.has(w))
    );
  const titleWords = toWords(title);
  const overlap = [];
  toWords(highlights).forEach((w) => {
    if (titleWords.has(w)) overlap.push(w);
  });
  return overlap;
};
